import { CheckAvailabilityResponse, MaxBookingTimeStatus } from './models/basket.model';

/**
 * Den maksimumtid, kurven løber ind i — kurvens egne tider lagt sammen pr. kvote og vindue.
 *
 * Ledighedstjekket kender kun én periode ad gangen, og kurvens tider findes endnu ikke på serveren
 * (`initCheckout` opretter dem med status `New`, som kvoten med vilje ikke tæller med). To tider,
 * der hver især er under kvoten, kan derfor tilsammen være over den, uden at nogen af svarene
 * melder `exceeded` — og så ville begge blive oprettet, og reglen ville være omgået.
 *
 * Tider grupperes pr. prisperiode og vindue (`scheduleId` + `windowStart`): det er dem, der deler
 * tallet, også når de ligger i forskellige lokaler under samme prisperiode. Nøglen er id'et og ikke
 * navnet, fordi to uafhængige prisperioder kan hedde det samme.
 *
 * En kvote, brugeren må overrule, spærrer ikke — det er administrationens sag, og en borger har det
 * aldrig; tjekket står her, så reglen er den samme i alle kurve.
 *
 * Samme regel som i administrationens foreningskurv
 * (`abook-admin-app/.../associations/components/basket/max-booking-time.ts`) og i den gamle
 * borgerfrontend (`abook-app/.../modules/booking-periods/max-booking-time.js`). De tre kopier lever
 * i hver sit projekt; skal de samles, hører de hjemme i `abook-library`.
 */
export function blockingBasketMaxBookingTime(
  responses: CheckAvailabilityResponse[],
  parseDate: (apiStr: string) => Date,
): string | null {
  const totals = new Map<
    string,
    { status: MaxBookingTimeStatus; requestedMinutes: number; lines: number }
  >();

  for (const response of responses ?? []) {
    const status = response?.maxBookingTime;

    if (!status || status.canOverride) {
      continue;
    }

    const key = `${status.scheduleId}|${status.windowStart}`;
    const total = totals.get(key);

    if (total) {
      total.requestedMinutes += status.requestedMinutes;
      total.lines += 1;
    } else {
      totals.set(key, { status, requestedMinutes: status.requestedMinutes, lines: 1 });
    }
  }

  for (const { status, requestedMinutes, lines } of totals.values()) {
    // `usedMinutes` er det samme for alle tider i gruppen: samme kvote, vindue og ejer.
    const total = status.usedMinutes + requestedMinutes;

    if (total <= status.maxMinutes) {
      continue;
    }

    // Er det én tid alene, der sprænger kvoten, har serveren allerede skrevet sætningen — den er
    // kilden til ordlyden. Kun summen af flere tider, som serveren ikke kan se, skrives her.
    if (lines === 1 && status.exceeded && status.message) {
      return status.message;
    }

    return quotaExceededMessage(status, total, parseDate);
  }

  return null;
}

/**
 * Samme sætning som API'et sender for én periode, nu for kurvens sum. Ordlyden er aftalt med
 * Ballerup og skal være den samme, uanset hvor den bliver til — se `BookingPeriodService` og
 * `BookingLimits` i API'et.
 */
function quotaExceededMessage(
  status: MaxBookingTimeStatus,
  totalMinutes: number,
  parseDate: (apiStr: string) => Date,
): string {
  return (
    `Du har samlet brugt ${formatMinutes(totalMinutes)} af de maks. ` +
    `${formatMinutes(status.maxMinutes)}, der kan bookes pr. ${status.period} ` +
    `${formatWindow(status, parseDate)}. ` +
    `Du har tidligere brugt ${formatMinutes(status.usedMinutes)}.`
  );
}

/** Minutter som en kvote omtales i — spejler `BookingLimits.FormatMinutes` i API'et. */
function formatMinutes(minutes: number): string {
  const whole = Math.round(minutes);
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;

  if (hours === 0) {
    return `${rest} min`;
  }

  if (rest !== 0) {
    return `${hours} t ${rest} min`;
  }

  return hours === 1 ? '1 time' : `${hours} timer`;
}

/**
 * Vinduet kvoten nulstilles i — spejler `FormatWindow` i API'et: et døgn nævnes med sin dato, en uge
 * og en måned med deres yderdage (slutdatoen er dagen efter perioden, så der trækkes et døgn fra).
 * `period` er API'ets egen etiket for spændet, se `BookingLimits.PeriodLabel`.
 */
function formatWindow(status: MaxBookingTimeStatus, parseDate: (apiStr: string) => Date): string {
  const start = parseDate(status.windowStart);

  if (status.period === 'døgn') {
    return `den ${formatDate(start)}`;
  }

  const end = parseDate(status.windowEnd);
  end.setDate(end.getDate() - 1);

  return `(${formatDate(start)} – ${formatDate(end)})`;
}

function formatDate(date: Date): string {
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');

  return `${day}-${month}-${date.getFullYear()}`;
}
